import React from 'react';
import { Eye, EyeOff, ArrowLeft, Mail, CheckCircle, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useForgotPasswordForm from '../../hooks/useForgotPasswordForm';
import SiteLogo from '../../components/SiteLogo';

/**
 * Forgot Password Variant — Minimal Card (Theme 2 "Modern")
 * Clean white card on soft gradient background with indigo/purple accents.
 * 3-step flow: email → verification code → new password.
 * Matches LoginMinimalCard / RegisterMinimalCard visual language.
 */
const ForgotPasswordMinimal = () => {
  const { t } = useTranslation('auth');
  const {
    step,
    email,
    code,
    password,
    confirmPassword,
    showPassword,
    showConfirmPassword,
    loading,
    error,
    resendTimer,
    codeInputRefs,
    setShowPassword,
    setShowConfirmPassword,
    handleEmailChange,
    handleSendCode,
    handleCodeChange,
    handleCodeKeyDown,
    handleCodePaste,
    handleVerifyCode,
    handlePasswordChange,
    handleConfirmPasswordChange,
    handleResetPassword,
    handleResendCode,
    goBackToEmail,
  } = useForgotPasswordForm();

  /* ── Step Indicator ─────────────────────────────────────── */
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((s) => (
        <React.Fragment key={s}>
          {/* Circle */}
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all duration-300 ${
              step > s
                ? 'bg-indigo-600 text-white'
                : step === s
                  ? 'bg-indigo-600 text-white ring-4 ring-indigo-100'
                  : 'bg-gray-100 text-gray-400'
            }`}
          >
            {step > s ? <CheckCircle size={16} /> : s}
          </div>
          {/* Connector */}
          {s < 3 && (
            <div
              className={`w-14 h-[3px] mx-1.5 rounded-full transition-colors duration-300 ${
                step > s ? 'bg-indigo-600' : 'bg-gray-200'
              }`}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );

  /* ── Step 1 — Enter Email ───────────────────────────────── */
  const renderStep1 = () => (
    <>
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-indigo-50 rounded-full mb-5">
          <Mail className="w-7 h-7 text-indigo-600" />
        </div>
        <h1 className="text-2xl font-extrabold text-gray-900 mb-2">
          {t('forgotPassword.title')}
        </h1>
        <p className="text-gray-500 text-[15px] leading-relaxed">
          {t('forgotPassword.descriptionAlt')}
        </p>
      </div>

      <form onSubmit={handleSendCode} className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            {t('forgotPassword.email')}
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => handleEmailChange(e.target.value)}
            placeholder={t('forgotPassword.enterEmail')}
            className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-2xl text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-indigo-600 focus:ring-4 focus:ring-indigo-100 transition-all"
            required
          />
        </div>

        {error && (
          <div className="p-3.5 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm font-medium">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-[15px] hover:bg-indigo-700 active:bg-indigo-800 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('forgotPassword.sending')}
            </>
          ) : (
            t('forgotPassword.sendCode')
          )}
        </button>
      </form>
    </>
  );

  /* ── Step 2 — Verify Code ───────────────────────────────── */
  const renderStep2 = () => (
    <>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-extrabold text-gray-900 mb-2">
          {t('forgotPassword.checkEmail')}
        </h1>
        <p className="text-gray-500 text-[15px] leading-relaxed">
          {t('forgotPassword.codeSentTo')}{' '}
          <span className="font-bold text-gray-900">{email}</span>
        </p>
      </div>

      <form onSubmit={handleVerifyCode} className="space-y-6">
        {/* 6-digit code inputs */}
        <div className="flex justify-center gap-2.5" onPaste={handleCodePaste}>
          {code.map((digit, index) => (
            <input
              key={index}
              ref={(el) => (codeInputRefs.current[index] = el)}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={digit}
              onChange={(e) => handleCodeChange(index, e.target.value)}
              onKeyDown={(e) => handleCodeKeyDown(index, e)}
              className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-xl bg-white focus:outline-none transition-all duration-200 ${
                digit
                  ? 'border-indigo-600 text-gray-900'
                  : 'border-gray-200 text-gray-900'
              } focus:border-indigo-600 focus:ring-4 focus:ring-indigo-100`}
            />
          ))}
        </div>

        {error && (
          <div className="p-3.5 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm font-medium text-center">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-[15px] hover:bg-indigo-700 active:bg-indigo-800 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('forgotPassword.verifying')}
            </>
          ) : (
            t('forgotPassword.verifyCode')
          )}
        </button>

        {/* Resend + different email */}
        <div className="text-center space-y-3">
          <p className="text-sm text-gray-500">
            {t('forgotPassword.didntReceive')}{' '}
            {resendTimer > 0 ? (
              <span className="text-gray-400 font-medium">
                {t('forgotPassword.resendIn', { seconds: resendTimer })}
              </span>
            ) : (
              <button
                type="button"
                onClick={handleResendCode}
                className="text-indigo-600 hover:text-indigo-700 font-semibold hover:underline transition-colors"
              >
                {t('forgotPassword.resendCode')}
              </button>
            )}
          </p>

          <button
            type="button"
            onClick={goBackToEmail}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
          >
            {t('forgotPassword.useDifferentEmail')}
          </button>
        </div>
      </form>
    </>
  );

  /* ── Step 3 — New Password ──────────────────────────────── */
  const renderStep3 = () => (
    <>
      <div className="text-center mb-8">
        <h1 className="text-2xl font-extrabold text-gray-900 mb-2">
          {t('forgotPassword.newPasswordTitle')}
        </h1>
        <p className="text-gray-500 text-[15px] leading-relaxed">
          {t('forgotPassword.newPasswordDescription')}
        </p>
      </div>

      <form onSubmit={handleResetPassword} className="space-y-5">
        {/* New Password */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            {t('forgotPassword.newPassword')}
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => handlePasswordChange(e.target.value)}
              placeholder={t('forgotPassword.newPassword')}
              className="w-full px-4 py-3.5 pe-12 bg-white border-2 border-gray-200 rounded-2xl text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-indigo-600 focus:ring-4 focus:ring-indigo-100 transition-all"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute end-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
          <p className="text-gray-400 text-xs mt-1.5 ms-1">
            {t('forgotPassword.passwordMinLength')}
          </p>
        </div>

        {/* Confirm Password */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">
            {t('forgotPassword.confirmNewPassword')}
          </label>
          <div className="relative">
            <input
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => handleConfirmPasswordChange(e.target.value)}
              placeholder={t('forgotPassword.confirmNewPassword')}
              className="w-full px-4 py-3.5 pe-12 bg-white border-2 border-gray-200 rounded-2xl text-[15px] text-gray-900 placeholder-gray-400 focus:outline-none focus:border-indigo-600 focus:ring-4 focus:ring-indigo-100 transition-all"
              required
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className="absolute end-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        {error && (
          <div className="p-3.5 bg-red-50 border border-red-200 rounded-2xl text-red-600 text-sm font-medium">
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={loading}
          className="w-full py-4 bg-indigo-600 text-white rounded-2xl font-bold text-[15px] hover:bg-indigo-700 active:bg-indigo-800 transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              {t('forgotPassword.resetting')}
            </>
          ) : (
            t('forgotPassword.resetPassword')
          )}
        </button>
      </form>
    </>
  );

  /* ── Main Render ────────────────────────────────────────── */
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-sky-50/40 to-indigo-50/30 flex flex-col overflow-y-auto">
      {/* Top Nav */}
      <nav className="flex items-center justify-between px-8 py-6 max-w-7xl mx-auto w-full">
        <Link to="/" className="flex items-center gap-2">
          <SiteLogo forceLight className="h-7" />
        </Link>
        <Link
          to="/login"
          className="flex items-center gap-2 text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="rtl:-scale-x-100"size={16} />
          {t('forgotPassword.backToLogin')}
        </Link>
      </nav>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          {/* Card */}
          <div className="bg-white rounded-3xl shadow-xl shadow-gray-200/60 border border-gray-100 p-10">
            {/* Step Indicator */}
            {renderStepIndicator()}

            {/* Step Content */}
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
          </div>

          {/* Below card */}
          <p className="text-center text-sm text-gray-500 mt-6">
            {t('forgotPassword.rememberPassword')}{' '}
            <Link to="/login" className="text-indigo-600 font-bold hover:underline">
              {t('forgotPassword.backToLogin')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordMinimal;
